import React, { useEffect, useState, useRef, useMemo } from "react";
import ReactCrop, {
  Crop as CropConfig,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import TextInputComp from "../../../components/customer/TextInputComp";
import MediaUploadComp from "../../../components/MediaUploadComp";
import CommonButton from "../../../components/CommonButton";
import TextAreaComp from "../../../components/customer/TextAreaComp";
import { 
    useCreateBlogMutation, 
    useLazyGetBlogByIdQuery, 
    useUpdateBlogMutation 
} from "../../../redux/actions/Admin/footerManagementAdmin";
import { useUploadFileMutation } from "../../../redux/actions/Admin/ServiceAdmin";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Sparkles, Globe, X, Check } from "lucide-react";
import { ICreateBlogRequest } from "../../../types/Request/AdminRequest";

// Helper to center the crop selection based on aspect ratio
function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number): CropConfig {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

// Generate cropped File using HTML5 Canvas & Pixel Crop Coordinates
function getCroppedImgFile(
  image: HTMLImageElement,
  crop: PixelCrop,
  fileName: string
): Promise<File> {
  const canvas = document.createElement("canvas");
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  canvas.width = Math.floor(crop.width * scaleX);
  canvas.height = Math.floor(crop.height * scaleY);

  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return Promise.reject(new Error("No 2d context"));
  }

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    canvas.width,
    canvas.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Canvas is empty"));
        return;
      }
      const file = new File([blob], fileName || "cropped-image.jpg", {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
      resolve(file);
    }, "image/jpeg", 0.95);
  });
}

const AddNewBlogScreen: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = !!id;

    // Mutation & Query Hooks
    const [createBlog, { isLoading: isCreating }] = useCreateBlogMutation();
    const [updateBlog, { isLoading: isUpdating }] = useUpdateBlogMutation();
    const [uploadFile, { isLoading: isUploadingFile }] = useUploadFileMutation();
    const [triggerFetchBlogDetails, { data: serverBlogData, isLoading: isLoadingDetails }] = useLazyGetBlogByIdQuery();

    const [form, setForm] = useState({
        title: "",
        slug: "",
        shortDescription: "",
        description: "",
        thumbnailUrl: "",
        bannerImageUrl: "",
        authorName: "Petland Team",
        metaTitle: "",
        metaDescription: "",
        isFeatured: false,
        isPublished: true,
    });

    // Modal & Cropper States
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);
    const [activeCropTarget, setActiveCropTarget] = useState<"thumbnailUrl" | "bannerImageUrl">("thumbnailUrl");
    const [cropOriginalFileName, setCropOriginalFileName] = useState("");
    const [crop, setCrop] = useState<CropConfig>();
    const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
    const imgRef = useRef<HTMLImageElement | null>(null);

    const isGlobalProcessing = isCreating || isUpdating || isLoadingDetails || isUploadingFile;

    // Rich Text Editor Modules Configuration
    const quillModules = useMemo(() => ({
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike", "blockquote"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            ["link", "image", "clean"]
        ]
    }), []);

    // Fetch blog details when editing
    useEffect(() => {
        if (isEditMode && id) {
            triggerFetchBlogDetails(Number(id));
        }
    }, [isEditMode, id, triggerFetchBlogDetails]);

    // Auto-generate slug and meta title from title
    const handleTitleChangeText = (text: string) => {
        const generatedSlug = text
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)+/g, "");
            
        setForm(prev => ({
            ...prev,
            title: text,
            slug: generatedSlug,
            metaTitle: `${text} | Petland DXB Guide`
        }));
    };

    // Populate form data on edit fetch
    useEffect(() => {
        const blog = serverBlogData?.value?.data;
        if (blog) {
            setForm({
                title: blog.title || "",
                slug: blog.slug || "",
                shortDescription: blog.shortDescription || "",
                description: blog.description || "",
                thumbnailUrl: blog.thumbnailUrl || "",
                bannerImageUrl: blog.bannerImageUrl || "",
                authorName: blog.authorName || "Petland Team",
                metaTitle: blog.metaTitle || "",
                metaDescription: blog.metaDescription || "",
                isFeatured: blog.isFeatured || false,
                isPublished: blog.isPublished || false,
            });
        }
    }, [serverBlogData]);

    // Step 1: User selects a file -> Load preview in modal
    const handleInitiateCrop = (file: File, fieldTarget: "thumbnailUrl" | "bannerImageUrl") => {
        const reader = new FileReader();
        reader.onload = () => {
            setCropImageSrc(reader.result as string);
            setActiveCropTarget(fieldTarget);
            setCropOriginalFileName(file.name);
            setCrop(undefined);
            setCropModalOpen(true);
        };
        reader.readAsDataURL(file);
    };

    // Step 2: Auto-position default center crop when image renders
    const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const { width, height } = e.currentTarget;
        const aspect = activeCropTarget === "thumbnailUrl" ? 16 / 10 : 21 / 9;
        setCrop(centerAspectCrop(width, height, aspect));
    };

    // Step 3: Crop and send to file upload mutation
    const handleConfirmCropAndUpload = async () => {
        if (!imgRef.current || !completedCrop) return;

        try {
            const croppedFile = await getCroppedImgFile(
                imgRef.current,
                completedCrop,
                cropOriginalFileName
            );

            setCropModalOpen(false);

            const response = await uploadFile({
                File: croppedFile,
                FolderName: `Blogs/${activeCropTarget === "thumbnailUrl" ? "Thumbnails" : "Banners"}`
            }).unwrap();

            setForm(prev => ({ ...prev, [activeCropTarget]: response.fileUrl }));
        } catch (error) {
            console.error("Failed to crop and upload image:", error);
        } finally {
            setCropImageSrc(null);
        }
    };

    const handleCloseCropModal = () => {
        setCropModalOpen(false);
        setCropImageSrc(null);
    };

    const handleFormSubmitAction = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.title.trim() || isGlobalProcessing) return;

        const requestPayload: ICreateBlogRequest = {
            ...form,
            title: form.title.trim(),
            slug: form.slug.trim(),
            publishedAt: new Date().toISOString()
        };

        try {
            if (isEditMode && id) {
                await updateBlog({ id: Number(id), ...requestPayload }).unwrap();
            } else {
                await createBlog(requestPayload).unwrap();
            }
            navigate(-1);
        } catch (error) {
            console.error("Failed to save blog:", error);
        }
    };

    const currentAspect = activeCropTarget === "thumbnailUrl" ? 16 / 10 : 21 / 9;

    return (
        <div className="p-4 md:p-6 lg:p-8 min-h-screen bg-[#FAFBF8] relative">
            
            {/* Global Loader */}
            {isGlobalProcessing && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-xs z-50 flex items-center justify-center">
                    <div className="bg-white rounded-2xl p-6 flex items-center gap-3 shadow-xl">
                        <Loader2 className="w-5 h-5 animate-spin text-[#2F5E2E]" />
                        <span className="font-semibold text-sm text-slate-700">Processing...</span>
                    </div>
                </div>
            )}

            {/* React Image Crop Modal */}
            {cropModalOpen && cropImageSrc && (
                <div className="fixed inset-0 bg-black/75 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Sparkles size={16} className="text-[#2F5E2E]" />
                                <h3 className="font-bold text-base text-slate-800">
                                    Crop {activeCropTarget === "thumbnailUrl" ? "Thumbnail (16:10)" : "Banner (21:9)"}
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={handleCloseCropModal}
                                className="p-1.5 rounded-full hover:bg-gray-100 text-gray-500 transition"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="relative w-full max-h-[65vh] overflow-auto bg-neutral-900 flex items-center justify-center p-4">
                            <ReactCrop
                                crop={crop}
                                onChange={(_, percentCrop) => setCrop(percentCrop)}
                                onComplete={(c) => setCompletedCrop(c)}
                                aspect={currentAspect}
                                className="max-h-[60vh]"
                            >
                                <img
                                    ref={imgRef}
                                    src={cropImageSrc}
                                    alt="Crop target"
                                    onLoad={onImageLoad}
                                    className="max-h-[55vh] object-contain mx-auto"
                                />
                            </ReactCrop>
                        </div>

                        <div className="p-4 bg-white border-t border-gray-100 flex justify-end items-center gap-3">
                            <CommonButton
                                text="Cancel"
                                variant="outline"
                                rounded="lg"
                                onClick={handleCloseCropModal}
                                className="px-5 text-xs text-gray-600 border-gray-200"
                            />
                            <CommonButton
                                text="Crop & Upload"
                                variant="filled"
                                rounded="lg"
                                icon={<Check size={14} />}
                                onClick={handleConfirmCropAndUpload}
                                className="bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white text-xs font-bold px-5 py-2"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Top Bar Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate(-1)}
                    disabled={isGlobalProcessing}
                    className="p-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition cursor-pointer disabled:opacity-40 shadow-2xs"
                >
                    <ArrowLeft size={16} />
                </button>

                <div className="text-left">
                    <h1 className="text-2xl font-black text-logoHeading">
                        {isEditMode ? "Edit Blog Post" : "Create New Blog"}
                    </h1>
                    <p className="text-xs text-gray-500 mt-0.5">
                        Fill in the details below to write and manage your blog article.
                    </p>
                </div>
            </div>

            {/* Main Form Fields */}
            <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-3xl p-5 shadow-xs">
                <form onSubmit={handleFormSubmitAction} className="space-y-6 text-left">
                    
                    {/* Basic Info */}
                    <div className="space-y-4">
                        <h2 className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center gap-1">
                            <Sparkles size={13} className="text-[#2F5E2E]" /> Blog Information
                        </h2>
                        
                        <TextInputComp
                            showLabel
                            placeholder="e.g. Best Healthy Food Options for Puppies"
                            value={form.title}
                            onChangeText={handleTitleChangeText}
                            label="Blog Title"
                            showStar
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <TextInputComp
                                showLabel
                                placeholder="best-healthy-food-for-puppies"
                                value={form.slug}
                                onChangeText={(text) => setForm({ ...form, slug: text })}
                                label="URL Slug"
                                showStar
                            />
                            <TextInputComp
                                showLabel
                                placeholder="e.g. Petland Team"
                                value={form.authorName}
                                onChangeText={(text) => setForm({ ...form, authorName: text })}
                                label="Author Name"
                                showStar
                            />
                        </div>
                    </div>

                    {/* Short Description */}
                    <TextAreaComp
                        showLabel
                        label="Short Summary"
                        showStar
                        placeholder="Write a brief overview of the blog..."
                        value={form.shortDescription}
                        onChangeText={(text) => setForm({ ...form, shortDescription: text })}
                    />

                    {/* Blog Content - ReactQuill Rich Text Editor */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-bold text-slate-700 block">
                            Blog Content <span className="text-red-500">*</span>
                        </label>
                        <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white shadow-2xs focus-within:border-[#2F5E2E] transition-colors">
                            <ReactQuill
                                theme="snow"
                                value={form.description}
                                onChange={(content) => setForm(prev => ({ ...prev, description: content }))}
                                modules={quillModules}
                                placeholder="Write and format your blog post content here..."
                                className="[&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-gray-100 [&_.ql-toolbar]:bg-slate-50/70 [&_.ql-container]:border-0 [&_.ql-editor]:min-h-[280px] [&_.ql-editor]:text-sm [&_.ql-editor]:text-slate-800"
                            />
                        </div>
                    </div>

                    {/* Media Uploads */}
                    <div className="space-y-4">
                        <h2 className="text-xs font-black uppercase tracking-wider text-slate-400">Media & Images</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                            <div className="md:col-span-1">
                                <MediaUploadComp
                                    label="Thumbnail Image"
                                    value={form.thumbnailUrl}
                                    onChange={(url) => setForm({ ...form, thumbnailUrl: url })}
                                    onFileSelect={(file) => handleInitiateCrop(file, "thumbnailUrl")}
                                    type="image"
                                    showStar
                                    aspectRatio="aspect-[16/10]"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <MediaUploadComp
                                    label="Banner Image"
                                    value={form.bannerImageUrl}
                                    onChange={(url) => setForm({ ...form, bannerImageUrl: url })}
                                    onFileSelect={(file) => handleInitiateCrop(file, "bannerImageUrl")}
                                    type="image"
                                    showStar
                                    aspectRatio="aspect-[21/9]"
                                />
                            </div>
                        </div>
                    </div>

                    {/* SEO Settings */}
                    <div className="bg-slate-50/50 border border-slate-100 p-4 rounded-2xl space-y-4">
                        <h2 className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center gap-1">
                            <Globe size={13} className="text-[#2F5E2E]" /> SEO Configuration
                        </h2>
                        <div className="space-y-4">
                            <TextInputComp
                                showLabel
                                placeholder="e.g. Best Dog Foods in Dubai"
                                value={form.metaTitle}
                                onChangeText={(text) => setForm({ ...form, metaTitle: text })}
                                label="Meta Title"
                            />
                            <TextAreaComp
                                showLabel
                                placeholder="Add a short description for search engines..."
                                value={form.metaDescription}
                                onChangeText={(text) => setForm({ ...form, metaDescription: text })}
                                label="Meta Description"
                            />
                        </div>
                    </div>

                    {/* Visibility Settings */}
                    <div className="flex flex-wrap items-center gap-6 select-none bg-[#FAFBF8] border p-3 rounded-xl border-dashed border-slate-200">
                        <div className="flex items-center cursor-pointer">
                            <input
                                id="isFeatured"
                                type="checkbox"
                                checked={form.isFeatured}
                                onChange={(e) => setForm({ ...form, isFeatured: e.target.checked })}
                                className="w-4 h-4 text-[#2F5E2E] border-gray-300 rounded focus:ring-[#6DBE45] cursor-pointer"
                            />
                            <label htmlFor="isFeatured" className="ml-2 text-xs font-bold text-slate-700 cursor-pointer">
                                Mark as Featured
                            </label>
                        </div>
                        <div className="flex items-center cursor-pointer">
                            <input
                                id="isPublished"
                                type="checkbox"
                                checked={form.isPublished}
                                onChange={(e) => setForm({ ...form, isPublished: e.target.checked })}
                                className="w-4 h-4 text-[#2F5E2E] border-gray-300 rounded focus:ring-[#6DBE45] cursor-pointer"
                            />
                            <label htmlFor="isPublished" className="ml-2 text-xs font-bold text-slate-700 cursor-pointer">
                                Publish Immediately
                            </label>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                        <CommonButton
                            text="Cancel"
                            variant="outline"
                            rounded="lg"
                            disabled={isGlobalProcessing}
                            onClick={() => navigate(-1)}
                            className="px-6 text-xs font-semibold text-gray-600 border-gray-200"
                        />
                        <CommonButton
                            text={isEditMode ? "Update Blog" : "Create Blog"}
                            variant="filled"
                            rounded="lg"
                            disabled={isGlobalProcessing || !form.title.trim()}
                            className="bg-[#2F5E2E] hover:bg-[#1f3f1e] text-white text-xs font-black px-6 py-2.5 shadow-sm"
                        />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddNewBlogScreen;